import type { ApiResponse } from '../models/ApiResponse';
import type { AuthResponse, LoginResponse, TwoFAResponse } from '../models/AuthModels';
import type {
  RegistrationFlowStatus,
  SamlRedirectResponse,
  TwoFAActivateResponse,
  TwoFARegistrationResponse,
  UserProfile,
} from './../models/AuthModels';
import BaseService from './BaseService';

class AuthService extends BaseService {
  public static login(username: string, password: string): Promise<ApiResponse<LoginResponse>> {
    return this.post('/v1/auth/login', { username, password });
  }

  public static logout(): Promise<ApiResponse<{ success: boolean; ssoLogoutUrl: string | null }>> {
    return this.post('/v1/auth/logout');
  }

  public static setPassword(password: string): Promise<ApiResponse<boolean>> {
    return this.post('/v1/auth/set-password', { credential: password });
  }

  public static resetPassword(password: string): Promise<ApiResponse<boolean>> {
    return this.post('/v1/auth/reset-password', { credential: password });
  }

  public static forgotPassword(email: string): Promise<ApiResponse<boolean>> {
    return this.post('/v1/auth/forgot-password', { email });
  }

  public static async refreshToken(secondTry?: boolean): Promise<ApiResponse<AuthResponse>> {
    return this.post('/v1/auth/token-refresh', null, { sent: secondTry, isTokenRefresh: true });
  }

  public static setUserDetails(body: Partial<UserProfile>): Promise<ApiResponse<LoginResponse>> {
    return this.post('/v1/auth/set-details', { ...body });
  }

  public static setup2FA(): Promise<ApiResponse<TwoFARegistrationResponse>> {
    return this.post('/v1/auth/two-factor/register');
  }

  public static activate2FA(pin: string): Promise<ApiResponse<TwoFAActivateResponse>> {
    return this.post('/v1/auth/two-factor/activate', { pin });
  }

  public static verify2FA(pin: string): Promise<ApiResponse<TwoFAResponse>> {
    return this.post('/v1/auth/two-factor/verify', { pin });
  }

  public static getRegistrationFlowStatus(): Promise<ApiResponse<RegistrationFlowStatus>> {
    return this.get('/v1/auth/register-status', { withCredentials: true });
  }

  public static async redirectWithSaml(accountId: string): Promise<ApiResponse<SamlRedirectResponse>> {
    return this.post('/v1/auth/redirect-with-saml', { accountId: accountId });
  }

  public static async exchangeSsoToken(): Promise<ApiResponse<LoginResponse>> {
    return this.post('/v1/auth/sso-session');
  }
}

export default AuthService;
